import React from "react";
import { Link, useNavigate } from "react-router-dom";
import "./Login.css";
import { Form, Layout, Row, Col, Input, Button } from "antd";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import useErrors from "hooks/useErrors";
import { authenticate } from "utils/auth";
import STUDENT_LOGIN from "graphql/mutations/StudentLogin";
import { setLanguageDataVar } from "cache";

import ErrorAlerting from "common/error_alerting";
import SocialLogin from "pages/new_sign_up/SocialLogin";
import STUDENT_LOGIN_WITH_OAUTH from "graphql/mutations/StudentLoginWithOauth";

const { Content } = Layout;

function LoginCard(props: any) {
  const navigate = useNavigate();
  const { setLoggedIn } = props;

  const { t, i18n } = useTranslation("login");
  const [login] = useMutation(STUDENT_LOGIN);
  const [loginWithOauth] = useMutation(STUDENT_LOGIN_WITH_OAUTH);
  const { errors, handleError, closeError } = useErrors();

  const handleLoginSuccess = (credentials: any, userData: any) => {
    const {
      givenName,
      familyName,
      locale,
      activeSubscription,
      currentLanguage: { id: languageId = "1" },
    } = userData;

    // Store credentials and log user in
    authenticate(credentials, {
      givenName,
      familyName,
      locale,
    });
    i18n.changeLanguage(userData.locale);
    setLoggedIn(true);
    setLanguageDataVar({
      studyLanguageId: languageId,
    });

    if (activeSubscription) {
      navigate("/");
    } else {
      navigate("/my-credits");
    }
  };

  const handleLogin = async (values: any) => {
    try {
      const { data } = await login({
        variables: values,
      });

      const {
        studentLogin: { credentials, authenticatable: userData },
      } = data;

      handleLoginSuccess(credentials, userData);
    } catch (e) {
      handleError(e);
    }
  };
  const handleOauthLogin = async (token: string) => {
    try {
      const { data } = await loginWithOauth({
        variables: { token },
      });

      const {
        studentLoginWithOauth: { credentials, authenticatable: userData },
      } = data;

      handleLoginSuccess(credentials, userData);
    } catch (e) {
      handleError(e);
    }
  };

  return (
    <Content className="content-container login-container">
      <div style={{ maxWidth: 400 }}>
        <ErrorAlerting errors={errors} onClose={closeError} />
      </div>
      <div className="social-login">
        <SocialLogin
          type="login"
          loading={{
            google: false,
            facebook: false,
          }}
          handleOauthLogin={handleOauthLogin}
        />
      </div>

      <div className="login-card">
        <div className="card-title">
          {t("Log into your account").toLocaleUpperCase()}
        </div>
        <Form
          name="login"
          initialValues={{ remember: true }}
          onFinish={(values) => handleLogin(values)}
        >
          <Form.Item
            name="email"
            rules={[{ required: true, message: "Email is required." }]}
          >
            <Input
              className="input"
              type="email"
              placeholder={t("Email")}
              name="email"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="password"
            rules={[{ required: true, message: "Password is required." }]}
          >
            <Input.Password
              className="input"
              type="password"
              placeholder={t("Password")}
              name="password"
              size="large"
            />
          </Form.Item>
          <Form.Item valuePropName="checked">
            <Row gutter={16}>
              <Col className="forgot-pwd-container" span={24}>
                <Link to="/reset-password" className="linked-text">
                  {t("Forgot password")}?
                </Link>
              </Col>
            </Row>
          </Form.Item>
          <Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Button
                  className="log-in-button"
                  type="primary"
                  htmlType="submit"
                >
                  {t("Login").toLocaleUpperCase()}
                </Button>
              </Col>
              <Col span={12}>
                <div className="login-prompt">
                  <span className="login-prompt-text">
                    {t("Not a member")}?
                  </span>
                  <a
                    href="https://flexiclasses.com/get-started/"
                    target="_blank"
                    className="linked-text"
                  >
                    {t("Register here").toLocaleUpperCase()}
                  </a>
                </div>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </Content>
  );
}

export default LoginCard;
